import React, { useState } from 'react';
import { API_BASE_URL } from '../../utils/apiBase';

interface Preview {
  id: string;
  audience: string;
  subject: string;
  text: string;
  html: string;
}

const sampleLabel: Record<string, string> = {
  'quotation-issued': 'Quotation',
  'quotation-follow-up': 'Quotation follow-up',
  'booking-secured': 'Deposit received',
  'shoot-confirmed': 'Confirmation',
  'booking-updated': 'Amendment',
  'shoot-reminder': 'Reminder',
  'deposit-reminder': 'Deposit reminder',
  'balance-requested': 'Balance due',
  'payment-received': 'Payment received',
  'paid-in-full': 'Paid in full',
  'studio-reply': 'Reply from Carmen',
  'client-message': 'Client wrote',
  reminder: 'Waiting for a reply',
  'client-update': 'Client updated',
};

// Sample links point at the live site, which refuses to be framed. Keep them
// visible but unclickable so the preview can never navigate away.
const inertLinks = (html: string) => html.replace(
  '</body>',
  '<style>a{pointer-events:none;cursor:default}</style></body>'
);

const labelFor = (preview: Preview) => sampleLabel[preview.id] || preview.subject;

const EmailPreviewGallery = () => {
  const [open, setOpen] = useState(false);
  const [previews, setPreviews] = useState<Preview[]>([]);
  const [selected, setSelected] = useState('');
  const [error, setError] = useState('');

  const load = async () => {
    setError('');
    const response = await fetch(`${API_BASE_URL}/studio/email-previews`);
    if (!response.ok) {
      setError('Email previews could not be loaded.');
      return;
    }
    const body = await response.json();
    const rows = Array.isArray(body.previews) ? body.previews : [];
    setPreviews(rows);
    setSelected(rows[0]?.id || '');
    setOpen(true);
  };

  const current = previews.find((preview) => preview.id === selected) || previews[0];
  const clientSamples = previews.filter((preview) => preview.audience !== 'studio');
  const studioSamples = previews.filter((preview) => preview.audience === 'studio');

  const options = (rows: Preview[]) => rows.map((preview) => (
    <option key={preview.id} value={preview.id}>{labelFor(preview)}</option>
  ));

  return (
    <section className="email-preview-gallery" aria-label="Email previews">
      <button type="button" className="btn-secondary btn-sm" onClick={load}>Preview emails</button>
      {error ? <p className="quote-history-note">{error}</p> : null}
      {open && current ? (
        <div className="email-preview-layout">
          <p>Sample wording only. Opening a preview does not send an email.</p>
          <label className="email-preview-picker">
            <span>Sample email</span>
            <select
              className="form-input"
              aria-label="Sample email"
              value={current.id}
              onChange={(event) => setSelected(event.target.value)}
            >
              {clientSamples.length > 0 ? <optgroup label="To the client">{options(clientSamples)}</optgroup> : null}
              {studioSamples.length > 0 ? <optgroup label="To Carmen">{options(studioSamples)}</optgroup> : null}
            </select>
          </label>
          <article className="email-preview-sheet">
            <p><strong>{current.subject}</strong></p>
            <p>{current.audience === 'studio' ? 'To Carmen' : 'To the client'}</p>
            <iframe title={current.subject} sandbox="" srcDoc={inertLinks(current.html)} />
          </article>
        </div>
      ) : null}
    </section>
  );
};

export default EmailPreviewGallery;
